import React, { useState, useCallback, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  FormLayout,
  Select,
  RangeSlider,
  LegacyStack,
  Banner,
  Text,
  Checkbox,
  TextField,
  Spinner
} from '@shopify/polaris';
import { updateSizechartSettings } from '../../services/sizechartService';
import { getAvailableAIModels } from '../../services/aiModelService';

export default function SizechartSettings({ shopId, settings, setSettings, refreshData }) {
  // State for settings
  const [defaultCountry, setDefaultCountry] = useState(settings?.defaultCountry || 'US');
  const [allowedDeviation, setAllowedDeviation] = useState(settings?.allowedDeviation || 3);
  const [selectedCountries, setSelectedCountries] = useState(settings?.countries || ['US', 'UK', 'EU', 'AU', 'NZ']);
  const [newCountry, setNewCountry] = useState({ code: '', name: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // AI model selection state
  const [aiModels, setAiModels] = useState([]);
  const [selectedAiModelId, setSelectedAiModelId] = useState(settings?.sizeConversionModelId || '');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  
  // Country options for select
  const defaultCountryOptions = [
    { label: 'United States (US)', value: 'US', name: 'United States' },
    { label: 'United Kingdom (UK)', value: 'UK', name: 'United Kingdom' },
    { label: 'European Union (EU)', value: 'EU', name: 'European Union' },
    { label: 'Australia (AU)', value: 'AU', name: 'Australia' },
    { label: 'New Zealand (NZ)', value: 'NZ', name: 'New Zealand' },
    { label: 'Canada (CA)', value: 'CA', name: 'Canada' },
    { label: 'Japan (JP)', value: 'JP', name: 'Japan' },
    { label: 'China (CN)', value: 'CN', name: 'China' },
    { label: 'South Korea (KR)', value: 'KR', name: 'South Korea' },
    { label: 'Russia (RU)', value: 'RU', name: 'Russia' },
    { label: 'Brazil (BR)', value: 'BR', name: 'Brazil' },
    { label: 'Mexico (MX)', value: 'MX', name: 'Mexico' },
    { label: 'India (IN)', value: 'IN', name: 'India' }
  ];
  
  // Combine default countries with any custom countries from settings
  const customCountries = settings?.customCountries || [];
  const allCountryOptions = [...defaultCountryOptions, ...customCountries.map(c => ({
    label: `${c.name} (${c.code})`,
    value: c.code,
    name: c.name,
    isCustom: true
  }))];
  
  // Filter out duplicates
  const countryOptions = allCountryOptions.filter((country, index, self) => 
    index === self.findIndex((c) => c.value === country.value)
  );
  
  // Handle country selection
  const handleCountrySelection = (country) => {
    return (checked) => {
      if (checked) {
        setSelectedCountries([...selectedCountries, country]);
      } else {
        setSelectedCountries(selectedCountries.filter(c => c !== country));
      }
    };
  };
  
  // Handle adding a new custom country
  const handleAddCustomCountry = () => {
    if (!newCountry.code || !newCountry.name) {
      setError('Country code and name are required');
      return;
    }
    
    // Check if country code already exists
    if (countryOptions.some(c => c.value.toLowerCase() === newCountry.code.toLowerCase())) {
      setError(`Country code ${newCountry.code} already exists`);
      return;
    }
    
    // Add to selected countries
    setSelectedCountries([...selectedCountries, newCountry.code]);
    
    // Reset form
    setNewCountry({ code: '', name: '' });
    setError('');
  };
  
  // Load available AI models
  useEffect(() => {
    const loadAiModels = async () => {
      try {
        setIsLoadingModels(true);
        const allModels = await getAvailableAIModels();
        
        // Filter to only include text models (exclude vision models)
        const textModels = allModels.filter(model => 
          model.type !== 'vision'
        );
        
        setAiModels(textModels);
        
        // If no model is selected and models are available, select the default or first one
        if (!selectedAiModelId && textModels.length > 0) {
          const defaultModel = textModels.find(m => m.isDefault) || textModels[0];
          setSelectedAiModelId(defaultModel.id);
        }
      } catch (error) {
        console.error('Error loading AI models:', error);
        setError('Failed to load AI models. Please try again.');
      } finally {
        setIsLoadingModels(false);
      }
    };
    
    loadAiModels();
  }, [selectedAiModelId]);
  
  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');
      
      // Validate inputs
      if (!defaultCountry) {
        setError('Default country is required');
        return;
      }
      
      if (selectedCountries.length === 0) {
        setError('At least one country must be selected');
        return;
      }
      
      // Ensure default country is in selected countries
      if (!selectedCountries.includes(defaultCountry)) {
        setSelectedCountries([...selectedCountries, defaultCountry]);
      }
      
      // Prepare settings data
      const settingsData = {
        defaultCountry,
        allowedDeviation,
        countries: selectedCountries,
        customCountries: settings?.customCountries || [],
        sizeConversionModelId: selectedAiModelId
      };
      
      const updatedSettings = await updateSizechartSettings(shopId, settingsData);
      
      setSettings(updatedSettings);
      setSuccessMessage('Settings saved successfully');
      refreshData();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div style={{ height: 'auto', overflow: 'visible' }}>
      <div style={{ marginBottom: '24px' }}>
        <Card padding="0">
          <div style={{ height: 'auto', overflow: 'visible' }}>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd' }}>
              <Text variant="headingMd">Sizechart Default Settings</Text>
            </div>
            <div style={{ padding: '16px' }}>
            {error && (
              <Banner status="critical" onDismiss={() => setError('')}>
                <p>{error}</p>
              </Banner>
            )}
            
            {successMessage && (
              <Banner status="success" onDismiss={() => setSuccessMessage('')}>
                <p>{successMessage}</p>
              </Banner>
            )}
            
            <FormLayout>
              <Select
                label="Default Country"
                options={countryOptions}
                value={defaultCountry}
                onChange={setDefaultCountry}
                helpText="This will be the default country for new sizecharts"
              />
              
              <RangeSlider
                label="Default Allowed Deviation"
                value={allowedDeviation}
                onChange={setAllowedDeviation}
                min={0}
                max={10}
                output
                suffix="cm"
                helpText="Default maximum allowed difference between master and product measurements"
              />
              
              <Text variant="headingMd">Supported Countries</Text>
              <LegacyStack vertical>
                {countryOptions.map(option => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    checked={selectedCountries.includes(option.value)}
                    onChange={handleCountrySelection(option.value)}
                  />
                ))}
              </LegacyStack>
              
              <Card.Section title="Add Custom Country">
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Country Code"
                      value={newCountry.code}
                      onChange={(value) => setNewCountry({...newCountry, code: value.toUpperCase()})}
                      helpText="2-letter country code (e.g., FR for France)"
                      maxLength={2}
                    />
                    
                    <TextField
                      label="Country Name"
                      value={newCountry.name}
                      onChange={(value) => setNewCountry({...newCountry, name: value})}
                      helpText="Full country name (e.g., France)"
                    />
                  </FormLayout.Group>
                  
                  <Button onClick={handleAddCustomCountry}>Add Country</Button>
                </FormLayout>
              </Card.Section>
              
              <Banner status="info">
                <p>These countries will be available for selection when creating and checking sizecharts.</p>
              </Banner>
              
              <Text variant="headingMd" as="h3" fontWeight="semibold" style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                AI Model Settings
              </Text>
              
              <Banner status="info">
                <p>Select which text AI model to use for size conversions. Different models have different credit costs and capabilities.</p>
              </Banner>
              
              {isLoadingModels ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spinner size="large" />
                  <div style={{ marginTop: '10px' }}>
                    <p>Loading available AI models...</p>
                  </div>
                </div>
              ) : aiModels.length > 0 ? (
                <Select
                  label="AI Model for Size Conversions"
                  options={aiModels.map(model => ({
                    label: `${model.displayName} (${model.creditCostPerOutput} credits per conversion)`,
                    value: model.id
                  }))}
                  value={selectedAiModelId}
                  onChange={setSelectedAiModelId}
                  helpText="This model will be used for all size conversions in the sizechart editor"
                />
              ) : (
                <Banner status="warning">
                  <p>No AI models available. Please contact your administrator.</p>
                </Banner>
              )}
              
              <div style={{ marginTop: '2rem' }}>
                <Button 
                  primary 
                  onClick={handleSaveSettings} 
                  loading={isSaving}
                >
                  Save Settings
                </Button>
              </div>
            </FormLayout>
            </div>
          </div>
        </Card>
      </div>
      

      
      <div style={{ marginBottom: '24px' }}>
        <Card padding="0">
          <div style={{ height: 'auto', overflow: 'visible' }}>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd' }}>
              <Text variant="headingMd">Size Conversion Information</Text>
            </div>
            <div style={{ padding: '16px' }}>
              <Banner status="success">
                <p>Size conversion between different country standards is now available! Use the Auto-Generate Country Sizes button in the sizechart editor to create comprehensive multi-country sizecharts.</p>
              </Banner>
            
            <div style={{ marginTop: '1rem' }}>
              <Text variant="headingMd">Common Size Equivalents</Text>
              <p style={{ marginTop: '0.5rem' }}>For reference, here are some common size equivalents across countries:</p>
              
              <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>US</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>UK</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>EU</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>AU/NZ</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>JP</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>CN</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>KR</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>XXS (00)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>2</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>30</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>2</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>3</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>145/70A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>33</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>XS (0-2)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>4-6</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>32-34</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>4-6</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>5-7</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>150/75A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>44</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>S (4-6)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>8-10</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>36-38</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>8-10</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>9-11</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>155/80A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>55</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>M (8-10)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>12-14</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>40-42</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>12-14</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>13-15</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>160/85A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>66</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>L (12-14)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>16-18</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>44-46</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>16-18</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>17-19</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>165/90A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>77</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>XL (16-18)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>20-22</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>48-50</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>20-22</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>21</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>170/95A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>88</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>XXL (20-22)</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>24-26</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>52-54</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>24-26</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>23</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>175/100A</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>99</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
